/**
 * AriaSetupScreen — S5 Soren setup
 * canonical: docs/v3_canonical.html lines 2233–2414
 *
 * Persisted via GET/POST /api/settings:
 *   company_settings.aria_preferences JSONB — action mode, threshold, SOREN, voice prefs
 *   auto_prelien_ca, reminder_* — column toggles (backend wired today)
 * Toggles without backend automation render disabled with "Coming soon".
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { brand } from '../../config/brand'
import {
  loadSettings,
  saveSettings,
  loadAriaClaudeConnectConfig,
  type CompanySettings,
  type AriaPreferences,
  type ActionMode,
  type AriaTrainingCounts,
  type AriaBillingStatus,
} from '../../api/settings'

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

const DEFAULT_ARIA_PREFS: AriaPreferences = {
  action_mode: 'suggest',
  confirmation_threshold: 10000,
  soren_enabled: false,
  voice_prefs: { voice_on_default: false, read_aloud: true },
}

function remindersEnabled(s: CompanySettings | undefined): boolean {
  if (!s) return true
  return (
    s.reminder_due !== false &&
    s.reminder_7before !== false &&
    s.reminder_7after !== false &&
    s.reminder_retention !== false
  )
}

function formatCount(n: number, singular: string): string {
  return `${n} ${singular}${n === 1 ? '' : 's'}`
}

function formatPoweredByLabel(billing: AriaBillingStatus | null | undefined): string {
  if (!billing) return `${brand.name} included`
  if (billing.mode === 'byo') return 'Your Claude account'
  if (billing.is_pro) return 'Soren Pro — unlimited'
  const used = billing.used_monthly ?? 0
  const total = billing.included_monthly ?? 10
  return `${brand.name} included (${used} of ${total} drafts this month)`
}

function PoweredBySection({
  billing,
  oauthEnabled,
  waitlistMessage,
}: {
  billing: AriaBillingStatus | null | undefined
  oauthEnabled: boolean
  waitlistMessage: string | null
}) {
  return (
    <div className="panel" data-testid="aria-powered-by-panel" style={{ marginBottom: 14 }}>
      <div className="panel-h">
        <div className="panel-tt">Powered by</div>
        <span className="panel-st">{formatPoweredByLabel(billing)}</span>
      </div>
      <div style={{ padding: '12px 16px' }}>
        <div
          style={{
            border: '1px solid var(--line)',
            borderRadius: 'var(--rs)',
            padding: '14px 16px',
            background: '#fff',
          }}
          data-testid="connect-claude-card"
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>
            Connect your Claude account
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.5, marginBottom: 12, maxWidth: 520 }}>
            Bring your own Anthropic account — your usage, your privacy, your billing. Optional BYO mode
            keeps drafts on your Claude subscription instead of included allowance.
          </div>
          {oauthEnabled ? (
            <button
              type="button"
              className="btn b-dk btn-sm"
              data-testid="connect-claude-btn"
              onClick={() => {
                /* OAuth URL wired when Anthropic approves developer program */
              }}
            >
              Connect your Claude account
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn b-ol btn-sm"
                disabled
                style={{ opacity: 0.55, cursor: 'not-allowed' }}
                data-testid="connect-claude-waitlist"
              >
                Coming soon — join the waitlist interest
              </button>
              {waitlistMessage && (
                <span style={{ fontSize: 11, color: 'var(--ink4)', maxWidth: 360, lineHeight: 1.45 }}>
                  {waitlistMessage}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ComingSoonTag() {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        color: 'var(--ink4)',
        background: 'var(--accent-l)',
        border: '1px solid var(--line)',
        borderRadius: 4,
        padding: '2px 7px',
        whiteSpace: 'nowrap',
      }}
    >
      Coming soon
    </span>
  )
}

function Toggle({
  on,
  onToggle,
  label,
  testId,
  disabled = false,
}: {
  on: boolean
  onToggle?: () => void
  label: string
  testId?: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      className={`toggle${on ? ' on' : ''}`}
      onClick={disabled ? undefined : onToggle}
      aria-pressed={on}
      aria-label={label}
      aria-disabled={disabled}
      disabled={disabled}
      style={disabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
      data-testid={testId}
    />
  )
}

function ActionModePicker({
  value,
  onChange,
}: {
  value: ActionMode
  onChange: (mode: ActionMode) => void
}) {
  const modes: { id: ActionMode; label: string }[] = [
    { id: 'ask', label: 'Ask first' },
    { id: 'suggest', label: 'Suggest' },
    { id: 'auto', label: 'Auto' },
  ]

  return (
    <div
      style={{ display: 'flex', gap: 0, border: '1px solid var(--line2)', borderRadius: 5, overflow: 'hidden' }}
      data-testid="action-mode-picker"
    >
      {modes.map(m => (
        <button
          key={m.id}
          type="button"
          className="btn"
          style={{
            borderRadius: 0,
            border: 'none',
            background: value === m.id ? 'var(--ink)' : '#fff',
            color: value === m.id ? '#fff' : 'var(--ink4)',
            padding: '5px 11px',
            fontSize: 11,
          }}
          onClick={() => onChange(m.id)}
          data-testid={`action-mode-${m.id}`}
        >
          {m.label}
        </button>
      ))}
    </div>
  )
}

function buildTrainingRows(counts: AriaTrainingCounts | undefined) {
  const c = counts ?? { contracts: 0, pay_apps: 0, approved_invoices: 0, rejected_invoices: 0 }
  return [
    {
      id: 'contracts',
      icon: '📄',
      title: 'Past contracts & SOVs',
      subtitle: 'PDF, DOCX, XLSX · so she understands your contract structure',
      countLabel: formatCount(c.contracts, 'uploaded'),
      action: 'add' as const,
    },
    {
      id: 'pay_apps',
      icon: '🧾',
      title: "Past pay apps you've sent",
      subtitle: 'PDF · teaches your formatting preferences and rounding habits',
      countLabel: formatCount(c.pay_apps, 'sent'),
      action: 'add' as const,
    },
    {
      id: 'approved',
      icon: '✓',
      title: 'Sub invoices you approved',
      subtitle: 'PDF or image · so she learns what "clean" looks like to you',
      countLabel: formatCount(c.approved_invoices, 'approved'),
      action: 'add' as const,
    },
    {
      id: 'rejected',
      icon: '✗',
      title: 'Sub invoices you rejected',
      subtitle: 'PDF or image · paste rejection reason if you have it',
      countLabel: formatCount(c.rejected_invoices, 'rejected'),
      action: 'add' as const,
    },
    {
      id: 'voice_memos',
      icon: '🎙',
      title: 'Voice memos · how you write reminders',
      subtitle: 'MP3, M4A, WAV · she learns your tone for follow-ups and emails',
      countLabel: '0 uploaded',
      action: 'add' as const,
    },
    {
      id: 'email_templates',
      icon: '✉',
      title: 'Email templates you use',
      subtitle: 'Paste your "please pay" or "missing waiver" wording',
      countLabel: '0 added',
      action: 'paste' as const,
    },
  ]
}

export default function AriaSetupScreen() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const thresholdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: loadSettings,
    staleTime: 60_000,
  })

  const { data: claudeConnect } = useQuery({
    queryKey: ['aria-claude-connect'],
    queryFn: loadAriaClaudeConnectConfig,
    staleTime: 30_000,
  })

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [saveErr, setSaveErr] = useState('')

  const [sorenOn, setSorenOn] = useState(DEFAULT_ARIA_PREFS.soren_enabled)
  const [actionMode, setActionMode] = useState<ActionMode>(DEFAULT_ARIA_PREFS.action_mode)
  const [autoSendReminders, setAutoSendReminders] = useState(true)
  const [autoPrelienCA, setAutoPrelienCA] = useState(true)
  const [confirmAbove, setConfirmAbove] = useState(String(DEFAULT_ARIA_PREFS.confirmation_threshold))
  const [voiceOnDefault, setVoiceOnDefault] = useState(DEFAULT_ARIA_PREFS.voice_prefs.voice_on_default)
  const [readAloud, setReadAloud] = useState(DEFAULT_ARIA_PREFS.voice_prefs.read_aloud)

  const trainingRows = useMemo(
    () => buildTrainingRows(settings?.aria_training_counts ?? undefined),
    [settings?.aria_training_counts],
  )

  useEffect(() => {
    if (!settings) return
    const prefs = { ...DEFAULT_ARIA_PREFS, ...(settings.aria_preferences ?? {}) }
    const vp = { ...DEFAULT_ARIA_PREFS.voice_prefs, ...(prefs.voice_prefs ?? {}) }
    setSorenOn(!!prefs.soren_enabled)
    setActionMode(prefs.action_mode)
    setConfirmAbove(String(prefs.confirmation_threshold))
    setVoiceOnDefault(!!vp.voice_on_default)
    setReadAloud(vp.read_aloud !== false)
    setAutoPrelienCA(settings.auto_prelien_ca !== false)
    setAutoSendReminders(remindersEnabled(settings))
  }, [settings])

  const persistSettings = useCallback(async (patch: Partial<CompanySettings>) => {
    setSaveStatus('saving')
    setSaveErr('')
    try {
      const updated = await saveSettings(patch)
      queryClient.setQueryData(['settings'], updated)
      setSaveStatus('saved')
      if (saveTimer.current) clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => setSaveStatus('idle'), 2500)
    } catch (e) {
      setSaveErr((e as Error).message || 'Save failed')
      setSaveStatus('error')
      if (saveTimer.current) clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => setSaveStatus('idle'), 4000)
    }
  }, [queryClient])

  const persistAriaPrefs = useCallback((patch: Partial<AriaPreferences>) => {
    const current = settings?.aria_preferences ?? DEFAULT_ARIA_PREFS
    const voicePatch = patch.voice_prefs
      ? { voice_prefs: { ...current.voice_prefs, ...patch.voice_prefs } }
      : {}
    void persistSettings({
      aria_preferences: { ...current, ...patch, ...voicePatch },
    })
  }, [persistSettings, settings?.aria_preferences])

  function handlePrelienToggle() {
    const next = !autoPrelienCA
    setAutoPrelienCA(next)
    void persistSettings({ auto_prelien_ca: next })
  }

  function handleRemindersToggle() {
    const next = !autoSendReminders
    setAutoSendReminders(next)
    void persistSettings({
      reminder_due: next,
      reminder_7before: next,
      reminder_7after: next,
      reminder_retention: next,
    })
  }

  function handleActionModeChange(mode: ActionMode) {
    setActionMode(mode)
    persistAriaPrefs({ action_mode: mode })
  }

  function handleSorenToggle() {
    const next = !sorenOn
    setSorenOn(next)
    persistAriaPrefs({ soren_enabled: next })
  }

  function handleThresholdChange(raw: string) {
    const digits = raw.replace(/\D/g, '')
    setConfirmAbove(digits)
    if (thresholdTimer.current) clearTimeout(thresholdTimer.current)
    thresholdTimer.current = setTimeout(() => {
      const amount = Math.max(0, parseInt(digits, 10) || 0)
      persistAriaPrefs({ confirmation_threshold: amount })
    }, 600)
  }

  function handleVoiceOnToggle() {
    const next = !voiceOnDefault
    setVoiceOnDefault(next)
    persistAriaPrefs({ voice_prefs: { voice_on_default: next, read_aloud: readAloud } })
  }

  function handleReadAloudToggle() {
    const next = !readAloud
    setReadAloud(next)
    persistAriaPrefs({ voice_prefs: { voice_on_default: voiceOnDefault, read_aloud: next } })
  }

  const wiredSaveLabel =
    saveStatus === 'saving' ? 'Saving…' :
    saveStatus === 'saved' ? 'Saved ✓' :
    saveStatus === 'error' ? 'Save failed' : null

  return (
    <>
      <div className="topbar">
        <div className="crumb">
          <b style={{ cursor: 'pointer' }} onClick={() => navigate('/settings')}>Settings</b>
          {' · Soren setup'}
        </div>
        <div className="topbar-r">
          <button
            type="button"
            className="btn b-gh btn-sm"
            onClick={() => navigate('/settings')}
            data-testid="aria-back-settings"
          >
            ← Back to settings
          </button>
        </div>
      </div>

      <div className="pc" style={{ maxWidth: 880, margin: '0 auto' }} data-testid="aria-setup-screen">
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink)', letterSpacing: '-.02em', marginBottom: 4 }}>
          Soren setup
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink3)', marginBottom: 18, maxWidth: 600, lineHeight: 1.5 }}>
          Soren learns from every action you take. Train her on past work so she predicts your way of doing
          things. Configure how proactive she can be.
        </div>

        <PoweredBySection
          billing={claudeConnect?.billing ?? settings?.aria_billing}
          oauthEnabled={!!claudeConnect?.oauth_enabled}
          waitlistMessage={claudeConnect?.waitlist_message ?? null}
        />

        <div className="soren-toggle" data-testid="soren-toggle-panel">
          <div>
            <div className="soren-tt">
              Replace Soren with SOREN <span className="soren-badge">Beta</span>
            </div>
            <div className="soren-st">
              Same brain, voice-first interface. SOREN listens, executes 1–3 actions per sentence, shows cards
              when uncertain instead of asking again. Renames &quot;Soren&quot; to &quot;SOREN&quot; in the UI. You can switch back
              any time.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, color: 'var(--ink4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' }}>
              {sorenOn ? 'On' : 'Off'}
            </span>
            <Toggle on={sorenOn} onToggle={handleSorenToggle} label="Replace Soren with SOREN" testId="toggle-soren" />
          </div>
        </div>

        <div className="panel" data-testid="aria-training-panel">
          <div className="panel-h">
            <div className="panel-tt">Train Soren on your work</div>
            <span className="panel-st">Upload past documents · she learns your formats, tone, and decisions</span>
          </div>
          <div>
            {trainingRows.map(row => (
              <div key={row.id} className="training-row" data-testid={`training-row-${row.id}`}>
                <div className="training-ic" aria-hidden>{row.icon}</div>
                <div>
                  <div className="training-tt">{row.title}</div>
                  <div className="training-st">{row.subtitle}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 10, color: 'var(--ink4)', fontWeight: 600 }}>{row.countLabel}</span>
                  <button
                    type="button"
                    className="btn b-ol btn-sm"
                    disabled
                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                    title="Coming soon"
                    data-testid={`training-${row.action}-${row.id}`}
                  >
                    Coming soon
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            background: 'var(--accent-l)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--rs)',
            padding: '10px 14px',
            marginBottom: 14,
            fontSize: 11,
            color: 'var(--ink3)',
            lineHeight: 1.55,
          }}
          data-testid="aria-privacy-callout"
        >
          <strong style={{ color: 'var(--ink)' }}>Privacy:</strong> Training data stays in your workspace.
          Soren&apos;s learning is private to your account — it doesn&apos;t train any shared model.
        </div>

        <div className="panel" data-testid="aria-behavior-panel">
          <div className="panel-h">
            <div className="panel-tt">Soren behavior</div>
            <span className="panel-st">How proactive should she be?</span>
            {wiredSaveLabel && (
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: 11,
                  fontWeight: 600,
                  color: saveStatus === 'error' ? 'var(--red)' : saveStatus === 'saved' ? 'var(--green)' : 'var(--ink4)',
                }}
                data-testid="aria-behavior-save-status"
              >
                {wiredSaveLabel}
              </span>
            )}
          </div>
          {settingsLoading && (
            <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--ink4)' }}>
              Loading saved preferences…
            </div>
          )}

          <div className="set-row">
            <div className="set-row-l">
              <div className="set-row-tt">Action mode</div>
              <div className="set-row-st">How Soren handles items in her queue</div>
            </div>
            <div className="set-row-r">
              <ActionModePicker value={actionMode} onChange={handleActionModeChange} />
            </div>
          </div>

          <div className="set-row" style={{ opacity: 0.72 }}>
            <div className="set-row-l">
              <div className="set-row-tt">Auto-approve clean sub invoices</div>
              <div className="set-row-st">Retention deducted + lien waiver attached + within budget</div>
            </div>
            <div className="set-row-r" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ComingSoonTag />
              <Toggle on={false} label="Auto-approve clean sub invoices" testId="toggle-auto-approve" disabled />
            </div>
          </div>

          <div className="set-row">
            <div className="set-row-l">
              <div className="set-row-tt">Auto-send payment reminders</div>
              <div className="set-row-st">Timed based on each owner&apos;s payment history</div>
            </div>
            <div className="set-row-r">
              <Toggle
                on={autoSendReminders}
                onToggle={handleRemindersToggle}
                label="Auto-send payment reminders"
                testId="toggle-auto-reminders"
              />
            </div>
          </div>

          <div className="set-row" style={{ opacity: 0.72 }}>
            <div className="set-row-l">
              <div className="set-row-tt">Auto-draft pay apps when subs are ready</div>
              <div className="set-row-st">When approved sub invoices add up beyond threshold</div>
            </div>
            <div className="set-row-r" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ComingSoonTag />
              <Toggle on={false} label="Auto-draft pay apps when subs are ready" testId="toggle-auto-draft" disabled />
            </div>
          </div>

          <div className="set-row">
            <div className="set-row-l">
              <div className="set-row-tt">Auto-file Pre Lien notice for new CA projects</div>
              <div className="set-row-st">Mailed via Lob within 20 days · $8.99 per notice</div>
            </div>
            <div className="set-row-r">
              <Toggle
                on={autoPrelienCA}
                onToggle={handlePrelienToggle}
                label="Auto-file Pre Lien notice for new CA projects"
                testId="toggle-auto-prelien"
              />
            </div>
          </div>

          <div className="set-row" style={{ opacity: 0.72 }}>
            <div className="set-row-l">
              <div className="set-row-tt">Coach subs after rejections</div>
              <div className="set-row-st">Sends a &quot;here&apos;s what to fix&quot; note when a sub&apos;s score drops</div>
            </div>
            <div className="set-row-r" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ComingSoonTag />
              <Toggle on={false} label="Coach subs after rejections" testId="toggle-coach-subs" disabled />
            </div>
          </div>

          <div className="set-row">
            <div className="set-row-l">
              <div className="set-row-tt">Confirmation required above</div>
              <div className="set-row-st">Always ask before actions over this dollar amount</div>
            </div>
            <div className="set-row-r">
              <input
                className="fld-i mono"
                value={`$${Number(confirmAbove.replace(/\D/g, '') || 0).toLocaleString()}`}
                onChange={e => handleThresholdChange(e.target.value)}
                style={{ width: 110, padding: '6px 10px', textAlign: 'right' }}
                data-testid="confirm-threshold-input"
              />
            </div>
          </div>
          {saveStatus === 'error' && saveErr && (
            <p style={{ color: 'var(--red)', fontSize: 11, margin: '0 16px 12px' }} data-testid="aria-behavior-save-error">
              {saveErr === 'No token' ? 'Session expired — please refresh the page.' : saveErr}
            </p>
          )}
        </div>

        <div className="panel" data-testid="aria-voice-panel">
          <div className="panel-h">
            <div className="panel-tt">Voice &amp; conversation</div>
            <span className="panel-st">For SOREN beta · ignored when Soren is text-only</span>
          </div>

          <div className="set-row">
            <div className="set-row-l">
              <div className="set-row-tt">Voice on by default</div>
              <div className="set-row-st">Mic visible on every screen when SOREN is enabled</div>
            </div>
            <div className="set-row-r">
              <Toggle
                on={voiceOnDefault}
                onToggle={handleVoiceOnToggle}
                label="Voice on by default"
                testId="toggle-voice-default"
              />
            </div>
          </div>

          <div className="set-row" style={{ opacity: 0.72 }}>
            <div className="set-row-l">
              <div className="set-row-tt">Wake word &quot;Hey SOREN&quot;</div>
              <div className="set-row-st">Always-on listening on desktop and mobile</div>
            </div>
            <div className="set-row-r" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ComingSoonTag />
              <Toggle on={false} label='Wake word "Hey SOREN"' testId="toggle-wake-word" disabled />
            </div>
          </div>

          <div className="set-row">
            <div className="set-row-l">
              <div className="set-row-tt">Read responses aloud</div>
              <div className="set-row-st">SOREN speaks back · turn off for silent mode</div>
            </div>
            <div className="set-row-r">
              <Toggle
                on={readAloud}
                onToggle={handleReadAloudToggle}
                label="Read responses aloud"
                testId="toggle-read-aloud"
              />
            </div>
          </div>

          <div className="set-row" style={{ opacity: 0.72 }}>
            <div className="set-row-l">
              <div className="set-row-tt">Voice profile</div>
              <div className="set-row-st">How SOREN sounds when she speaks back</div>
            </div>
            <div className="set-row-r" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ComingSoonTag />
              <select
                className="fld-i"
                style={{ width: 170, padding: '6px 10px', opacity: 0.5, cursor: 'not-allowed' }}
                value="calm"
                disabled
                data-testid="voice-profile-select"
              >
                <option value="calm">Calm · default</option>
              </select>
            </div>
          </div>
        </div>

        <div className="panel" data-testid="aria-phone-panel">
          <div className="panel-h">
            <div className="panel-tt">Call SOREN from your phone</div>
            <span className="panel-st">Coming in v4 · same brain, no screen needed</span>
          </div>
          <div className="panel-bp">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink2)', marginBottom: 3 }}>
                  Your SOREN number will be:
                </div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink5)' }}>
                  to be assigned
                </div>
              </div>
              <button type="button" className="btn b-ol btn-sm" disabled style={{ opacity: 0.5 }}>
                Coming soon
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
